<script setup lang="ts">
import { computed, ref } from 'vue';
import { useMutation, useQuery } from '@tanstack/vue-query';
import {
  patientCaregiverPreferencesApi,
  type PatientCaregiverPreference,
  type PreferenceKind,
} from '@/api/patient-caregiver-preferences';
import { caregiversApi, type Caregiver } from '@/api/caregivers';
import { getCareerColor } from '@/utils/careers';
import SectionHeader from '@/components/section-header.vue';

interface Props {
  patientId: number;
}

const props = defineProps<Props>();

const errorMessage = ref<string | null>(null);
const selectedBlockedCaregiverId = ref<number | null>(null);
const selectedFavoriteCaregiverId = ref<number | null>(null);

const { data: preferences, refetch, isPending } = useQuery({
  queryKey: ['patientCaregiverPreferences', props.patientId],
  queryFn: () => patientCaregiverPreferencesApi.index(props.patientId),
  initialData: [] as PatientCaregiverPreference[],
});

const { data: caregivers } = useQuery({
  queryKey: ['caregivers'],
  queryFn: () => caregiversApi.index(),
  initialData: [] as Caregiver[],
});

const blockedPreferences = computed(
  () => preferences.value.filter((preference) => preference.kind === 'blocked'),
);
const favoritePreferences = computed(
  () => preferences.value.filter((preference) => preference.kind === 'favorite'),
);

function availableCaregivers(kind: PreferenceKind) {
  const usedIds = new Set(preferences.value.map((preference) => preference.caregiverId));

  return caregivers.value
    .filter((caregiver) => !usedIds.has(caregiver.id))
    .map((caregiver) => ({
      title: `${caregiver.fullName} (${caregiver.humanCareer})`,
      value: caregiver.id,
      kind,
    }));
}

const blockedOptions = computed(() => availableCaregivers('blocked'));
const favoriteOptions = computed(() => availableCaregivers('favorite'));

function extractErrors(error: unknown): string {
  const errors = (error as { response?: { data?: { errors?: unknown } } })?.response?.data?.errors;
  if (Array.isArray(errors)) return errors.join(', ');
  if (errors instanceof Object) return Object.values(errors).flat().join(', ');

  return 'Ocurrió un error. Intenta nuevamente.';
}

const { mutate: createPreference, isPending: isCreating } = useMutation({
  mutationFn: ({ kind, caregiverId }: { kind: PreferenceKind, caregiverId: number }) =>
    patientCaregiverPreferencesApi.create(props.patientId, { kind, caregiverId }),
  onSuccess: () => {
    errorMessage.value = null;
    selectedBlockedCaregiverId.value = null;
    selectedFavoriteCaregiverId.value = null;
    refetch();
  },
  onError: (error: unknown) => {
    errorMessage.value = extractErrors(error);
  },
});

const { mutate: destroyPreference } = useMutation({
  mutationFn: (id: number) => patientCaregiverPreferencesApi.destroy(props.patientId, id),
  onSuccess: () => {
    errorMessage.value = null;
    refetch();
  },
  onError: (error: unknown) => {
    errorMessage.value = extractErrors(error);
  },
});

function addBlocked() {
  if (!selectedBlockedCaregiverId.value) return;
  createPreference({ kind: 'blocked', caregiverId: selectedBlockedCaregiverId.value });
}

function addFavorite() {
  if (!selectedFavoriteCaregiverId.value) return;
  createPreference({ kind: 'favorite', caregiverId: selectedFavoriteCaregiverId.value });
}
</script>
<template>
  <div>
    <v-alert
      v-if="errorMessage"
      type="error"
      variant="tonal"
      closable
      class="mb-4"
      @click:close="errorMessage = null"
    >
      {{ errorMessage }}
    </v-alert>

    <section-header
      icon="mdi-star"
      title="Profesionales Favoritos"
      class="mb-2"
    />
    <p class="text-body-2 text-medium-emphasis mb-4">
      Los planes de ruta priorizan a estos profesionales para las visitas del paciente.
    </p>
    <div class="d-flex align-center ga-2 mb-3 preference-form">
      <v-autocomplete
        v-model="selectedFavoriteCaregiverId"
        :items="favoriteOptions"
        label="Agregar favorito"
        density="compact"
        variant="outlined"
        hide-details
        clearable
      />
      <v-btn
        color="primary"
        variant="tonal"
        :disabled="!selectedFavoriteCaregiverId"
        :loading="isCreating"
        @click="addFavorite"
      >
        Agregar
      </v-btn>
    </div>
    <v-list
      v-if="favoritePreferences.length > 0"
      density="compact"
      class="mb-6"
    >
      <v-list-item
        v-for="preference in favoritePreferences"
        :key="preference.id"
        :title="preference.caregiverFullName"
      >
        <template #prepend>
          <v-icon
            icon="mdi-star"
            color="amber-darken-2"
          />
        </template>
        <template #subtitle>
          <v-chip
            v-if="preference.humanCaregiverCareer"
            size="x-small"
            variant="tonal"
            :color="getCareerColor(preference.caregiverCareer || '')"
            class="mt-1"
          >
            {{ preference.humanCaregiverCareer }}
          </v-chip>
        </template>
        <template #append>
          <v-btn
            icon="mdi-delete"
            size="small"
            variant="text"
            color="error"
            @click="destroyPreference(preference.id)"
          />
        </template>
      </v-list-item>
    </v-list>
    <p
      v-else-if="!isPending"
      class="text-body-2 text-medium-emphasis mb-6"
    >
      No hay profesionales favoritos registrados
    </p>

    <v-divider class="mb-6" />

    <section-header
      icon="mdi-account-cancel"
      title="Profesionales Bloqueados"
      class="mb-2"
    />
    <p class="text-body-2 text-medium-emphasis mb-4">
      Estos profesionales nunca serán asignados al paciente en los planes de ruta.
    </p>
    <div class="d-flex align-center ga-2 mb-3 preference-form">
      <v-autocomplete
        v-model="selectedBlockedCaregiverId"
        :items="blockedOptions"
        label="Agregar bloqueado"
        density="compact"
        variant="outlined"
        hide-details
        clearable
      />
      <v-btn
        color="primary"
        variant="tonal"
        :disabled="!selectedBlockedCaregiverId"
        :loading="isCreating"
        @click="addBlocked"
      >
        Agregar
      </v-btn>
    </div>
    <v-list
      v-if="blockedPreferences.length > 0"
      density="compact"
    >
      <v-list-item
        v-for="preference in blockedPreferences"
        :key="preference.id"
        :title="preference.caregiverFullName"
      >
        <template #prepend>
          <v-icon
            icon="mdi-account-cancel"
            color="error"
          />
        </template>
        <template #subtitle>
          <v-chip
            v-if="preference.humanCaregiverCareer"
            size="x-small"
            variant="tonal"
            :color="getCareerColor(preference.caregiverCareer || '')"
            class="mt-1"
          >
            {{ preference.humanCaregiverCareer }}
          </v-chip>
        </template>
        <template #append>
          <v-btn
            icon="mdi-delete"
            size="small"
            variant="text"
            color="error"
            @click="destroyPreference(preference.id)"
          />
        </template>
      </v-list-item>
    </v-list>
    <p
      v-else-if="!isPending"
      class="text-body-2 text-medium-emphasis"
    >
      No hay profesionales bloqueados registrados
    </p>
  </div>
</template>

<style scoped>
.preference-form {
  max-width: 520px;
}
</style>
